import type { PersonProcessStats, ProcessSummary } from "../../api/types";
import { median } from "./processDurationStats";

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

const earliest = (a: string, b: string) => (Date.parse(b) < Date.parse(a) ? b : a);
const latest = (a: string, b: string) => (Date.parse(b) > Date.parse(a) ? b : a);

/** The people who ran `process` within the list's scope. */
export const processPeople = (process: ProcessSummary): PersonProcessStats[] =>
  process.people ?? [];

/** Seconds `person` spent on the process across their runs. */
export const personSeconds = (person: PersonProcessStats) => sum(person.runSeconds);

/**
 * `process` as the given people alone show it, added up from the list's per-person figures; `null`
 * when none of them ran it. Name, description, aliases and step names stay the whole list's.
 */
export const summaryForPeople = (
  process: ProcessSummary,
  userIds: ReadonlySet<string>,
): ProcessSummary | null => {
  const everyone = processPeople(process);
  const people = everyone.filter(person => userIds.has(person.userId));
  if (!people.length) return null;
  if (people.length === everyone.length) return process;
  const runs = people.flatMap(person => person.runSeconds);
  const total = sum(runs);
  const appSeconds: Record<string, number> = {};
  for (const person of people) {
    for (const [app, seconds] of Object.entries(person.appSeconds)) {
      appSeconds[app] = (appSeconds[app] ?? 0) + seconds;
    }
  }
  return {
    ...process,
    people,
    frequency: runs.length,
    avgDuration: total / runs.length,
    totalDuration: total,
    medianDuration: median([...runs].sort((a, b) => a - b)),
    stepCount: sum(people.map(person => person.stepCount)),
    firstSeen: people.map(person => person.firstSeen).reduce(earliest),
    lastSeen: people.map(person => person.lastSeen).reduce(latest),
    appSeconds,
    apps: Object.keys(appSeconds).sort(),
  };
};

/** The processes the given people ran, each as they alone show it. */
export const summariesForPeople = (
  processes: ProcessSummary[],
  userIds: readonly string[],
): ProcessSummary[] => {
  const ids = new Set(userIds);
  return processes.flatMap(process => summaryForPeople(process, ids) ?? []);
};

/** Each person's own processes, as a list for them alone shows them. */
export const processesByPerson = (
  processes: ProcessSummary[],
  userIds: readonly string[],
): Record<string, ProcessSummary[]> =>
  Object.fromEntries(userIds.map(id => [id, summariesForPeople(processes, [id])]));
